class UseAndCare extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  /** @type {HTMLAnchorElement[]} */
  #links = [];

  /** @type {HTMLElement[]} */
  #sections = [];

  /** @type {HTMLElement | Window} */
  #scrollContainer = window;

  /** @type {IntersectionObserver | undefined} */
  #sectionObserver;

  connectedCallback() {
    this.#controller?.abort();
    this.#sectionObserver?.disconnect();
    this.#controller = new AbortController();

    const { signal } = this.#controller;
    this.#links = Array.from(this.querySelectorAll('[data-use-care-nav-link]')).filter(
      (link) => link instanceof HTMLAnchorElement
    );
    this.#sections = Array.from(this.querySelectorAll('[data-use-care-section]')).filter(
      (section) => section instanceof HTMLElement
    );

    const pageWrapper = this.closest('.page-wrapper');
    const pageWrapperStyles = pageWrapper instanceof HTMLElement ? window.getComputedStyle(pageWrapper) : null;
    const pageWrapperIsScrollable =
      pageWrapper instanceof HTMLElement &&
      pageWrapper.scrollHeight > pageWrapper.clientHeight &&
      ['auto', 'scroll', 'overlay'].includes(pageWrapperStyles?.overflowY ?? '');
    this.#scrollContainer = pageWrapperIsScrollable ? pageWrapper : window;

    this.#links.forEach((link) => {
      link.addEventListener('click', (event) => this.#scrollToSection(event, link), { signal });
    });

    this.#scrollContainer.addEventListener('scroll', () => this.#syncActiveLink(), { passive: true, signal });
    window.addEventListener('resize', () => this.#syncActiveLink(), { passive: true, signal });
    window.addEventListener('pageshow', () => this.#syncActiveLink(), { signal });

    if ('IntersectionObserver' in window) {
      this.#sectionObserver = new IntersectionObserver(() => this.#syncActiveLink(), {
        root: this.#scrollContainer instanceof HTMLElement ? this.#scrollContainer : null,
        rootMargin: `-${this.#headerOffset()}px 0px -60% 0px`,
        threshold: [0, 1],
      });
      this.#sections.forEach((section) => this.#sectionObserver?.observe(section));
    }

    this.#syncActiveLink();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#sectionObserver?.disconnect();
  }

  /** @param {MouseEvent} event @param {HTMLAnchorElement} link */
  #scrollToSection(event, link) {
    const targetId = link.hash.slice(1);
    const target = targetId ? this.querySelector(`#${CSS.escape(targetId)}`) : null;
    if (!(target instanceof HTMLElement)) return;

    event.preventDefault();
    this.#setActiveLink(link);

    const smoothScrollEnabled = this.dataset.smoothScroll === 'true';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: smoothScrollEnabled && !reducedMotion ? 'smooth' : 'auto', block: 'start' });

    if (window.history?.replaceState) {
      window.history.replaceState(null, '', `#${targetId}`);
    }
  }

  #syncActiveLink() {
    if (this.#sections.length === 0) return;

    let activeSection = this.#sections[0];
    const isElementScroller = this.#scrollContainer instanceof HTMLElement;
    const scrollTop = isElementScroller ? this.#scrollContainer.scrollTop : window.scrollY;
    const viewportHeight = isElementScroller ? this.#scrollContainer.clientHeight : window.innerHeight;
    const scrollHeight = isElementScroller
      ? this.#scrollContainer.scrollHeight
      : document.documentElement.scrollHeight;
    const activationTop = isElementScroller
      ? this.#scrollContainer.getBoundingClientRect().top + this.#headerOffset()
      : this.#headerOffset();
    const nearBottom = viewportHeight + scrollTop >= scrollHeight - 4;

    if (nearBottom) {
      activeSection = this.#sections[this.#sections.length - 1];
    } else {
      this.#sections.forEach((section) => {
        if (section.getBoundingClientRect().top <= activationTop + 2) activeSection = section;
      });
    }

    const activeLink = this.#links.find((link) => link.hash === `#${activeSection.id}`);
    if (activeLink) this.#setActiveLink(activeLink);
  }

  /** @param {HTMLAnchorElement} activeLink */
  #setActiveLink(activeLink) {
    const parentKey = activeLink.dataset.useCareParentKey;
    const parentLink = parentKey
      ? this.#links.find((link) => link.dataset.useCareSectionKey === parentKey)
      : undefined;
    const activeLinks = new Set([activeLink, parentLink].filter(Boolean));
    const activeLinkChanged = activeLink.getAttribute('aria-current') !== 'true';

    this.#links.forEach((link) => {
      if (activeLinks.has(link)) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    });

    if (activeLinkChanged) this.#keepPrimaryLinkVisible(parentLink ?? activeLink);
  }

  /** @param {HTMLAnchorElement} activeLink */
  #keepPrimaryLinkVisible(activeLink) {
    if (!window.matchMedia('(max-width: 900px)').matches) return;

    const navigation = activeLink.closest('[data-use-care-navigation]');
    if (!(navigation instanceof HTMLElement) || navigation.scrollWidth <= navigation.clientWidth) return;

    const navigationRect = navigation.getBoundingClientRect();
    const activeLinkRect = activeLink.getBoundingClientRect();
    let nextScrollLeft = navigation.scrollLeft;

    if (activeLinkRect.left < navigationRect.left) {
      nextScrollLeft -= navigationRect.left - activeLinkRect.left;
    } else if (activeLinkRect.right > navigationRect.right) {
      nextScrollLeft += activeLinkRect.right - navigationRect.right;
    } else {
      return;
    }

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    navigation.scrollTo({ left: nextScrollLeft, behavior: reducedMotion ? 'auto' : 'smooth' });
  }

  #headerOffset() {
    const headerHeight = Number.parseFloat(
      window.getComputedStyle(document.body).getPropertyValue('--header-height')
    );
    return (Number.isFinite(headerHeight) ? headerHeight : 0) + 24;
  }
}

if (!customElements.get('use-and-care')) {
  customElements.define('use-and-care', UseAndCare);
}
